import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  ClientQuestionSet,
  ClientResponse,
  CreateQuestionSetRequest,
  QuestionSetWithProject
} from '../types/questions';
import { emailService } from '../services/email';
import { createQuestionnaireEmail } from '../utils/emailTemplates';

export function useClientQuestions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Créer un nouveau set de questions
  const createQuestionSet = useCallback(async (data: CreateQuestionSetRequest) => {
    if (!user?.id) {
      throw new Error('Utilisateur non authentifié');
    }

    setLoading(true);
    setError(null);

    try {
      // Générer un token d'accès sécurisé
      const accessToken = await generateAccessToken();

      // Calculer la date d'expiration si spécifiée
      let expiresAt = null;
      if (data.expires_in_days) {
        const expireDate = new Date();
        expireDate.setDate(expireDate.getDate() + data.expires_in_days);
        expiresAt = expireDate.toISOString();
      }

      const { data: questionSet, error: createError } = await supabase
        .from('client_questions')
        .insert({
          project_id: data.project_id,
          admin_id: user.id,
          client_id: data.client_id,
          title: data.title,
          questions: data.questions,
          access_token: accessToken,
          expires_at: expiresAt,
        })
        .select()
        .single();

      if (createError) throw createError;

      return questionSet as ClientQuestionSet;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création des questions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Récupérer les questions d'un projet
  const getProjectQuestions = useCallback(async (projectId: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('client_questions')
        .select(`
          *,
          project:projects(id, title, description)
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Récupérer séparément les informations client
      if (data && data.length > 0) {
        const clientIds = [...new Set(data.map(q => q.client_id))];
        const { data: clientData, error: clientError } = await supabase
          .from('users')
          .select('id, full_name, email')
          .in('id', clientIds);

        if (clientError) {
          console.warn('Erreur lors de la récupération des clients:', clientError);
        }

        // Associer les données client aux questions
        const questionsWithClients = data.map(question => ({
          ...question,
          client: clientData?.find(client => client.id === question.client_id) || null
        }));

        return questionsWithClients as QuestionSetWithProject[];
      }

      return data as QuestionSetWithProject[];
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la récupération des questions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Récupérer les réponses d'un set de questions
  const getQuestionResponses = useCallback(async (questionSetId: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('client_responses')
        .select('*')
        .eq('question_set_id', questionSetId)
        .order('responded_at', { ascending: false });

      if (fetchError) throw fetchError;

      return data as ClientResponse[];
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la récupération des réponses');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Récupérer une question par token d'accès (pour le client)
  const getQuestionByToken = useCallback(async (token: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('client_questions')
        .select(`
          *,
          project:projects(id, title, description),
          responses:client_responses(*)
        `)
        .eq('access_token', token)
        .eq('status', 'pending')
        .single();

      if (fetchError) throw fetchError;

      // Vérifier si la question n'est pas expirée
      if (data.expires_at && new Date(data.expires_at) < new Date()) {
        throw new Error('Ce lien de questions a expiré');
      }

      return data as QuestionSetWithProject;
    } catch (err: any) {
      setError(err.message || 'Questions introuvables ou expirées');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Soumettre des réponses (pour le client)
  const submitResponses = useCallback(async (
    questionSetId: string,
    responses: Record<string, string>
  ) => {
    setLoading(true);
    setError(null);

    try {
      // 1. Récupérer les informations du questionnaire
      const { data: questionSet, error: fetchError } = await supabase
        .from('client_questions')
        .select(`
          *,
          project:projects(id, title)
        `)
        .eq('id', questionSetId)
        .single();

      if (fetchError) throw fetchError;

      // 2. Soumettre les réponses
      const { data: responseData, error: submitError } = await supabase
        .from('client_responses')
        .insert({
          question_set_id: questionSetId,
          responses,
          client_ip: await getClientIP(),
          user_agent: navigator.userAgent
        })
        .select()
        .single();

      if (submitError) throw submitError;

      // 3. Créer automatiquement une demande d'intervention
      const { error: interventionError } = await supabase
        .from('client_requests')
        .insert({
          user_id: questionSet.client_id,
          request_type: 'question_response',
          title: `Réponses au questionnaire: ${questionSet.title}`,
          description: `Le client a répondu au questionnaire "${questionSet.title}" pour le projet ${questionSet.project?.title || 'Non spécifié'}`,
          data: {
            question_set_id: questionSetId,
            question_title: questionSet.title,
            project_id: questionSet.project_id,
            project_title: questionSet.project?.title,
            questions: questionSet.questions,
            responses: responses,
            submitted_at: new Date().toISOString()
          },
          status: 'pending',
          priority: 'medium',
          submitted_at: new Date().toISOString()
        });

      if (interventionError) {
        console.error('Erreur lors de la création de la demande d\'intervention:', interventionError);
        // On ne throw pas l'erreur pour ne pas bloquer la soumission des réponses
      }

      return responseData as ClientResponse;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi des réponses');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Envoyer l'email avec le lien vers les questions
  const sendQuestionEmail = useCallback(async (
    questionSetId: string,
    clientEmail: string,
    clientName: string,
    projectTitle: string,
    questionTitle: string,
    projectId?: string
  ) => {
    setLoading(true);
    setError(null);

    try {
      // Récupérer le token d'accès
      const { data: questionSet, error: fetchError } = await supabase
        .from('client_questions')
        .select('access_token')
        .eq('id', questionSetId)
        .single();

      if (fetchError) throw fetchError;

      // Construire l'URL d'accès aux questions (côté client, pas admin)
      const clientBaseUrl = import.meta.env.VITE_CLIENT_URL || 'https://client.lessablesweb.fr';
      const questionUrl = `${clientBaseUrl}/questions/${questionSet.access_token}`;

      // Générer le HTML avec le template
      const htmlContent = createQuestionnaireEmail({
        clientName,
        projectTitle,
        questionnaireUrl: questionUrl,
      });

      // Envoyer l'email avec le questionnaire
      const emailRequest = {
        to: clientEmail,
        toName: clientName,
        subject: `Questions sur votre projet: ${projectTitle}`,
        html: htmlContent,
        text: `Questions sur votre projet: ${projectTitle}

Bonjour ${clientName},

Nous avons quelques questions concernant votre projet "${projectTitle}" pour mieux comprendre vos besoins.

${questionTitle}

Répondez en ligne: ${questionUrl}

Merci de prendre le temps de répondre à ces questions.

Cordialement,
L'équipe LesSablesWeb`,
        referenceType: 'client_question' as const,
        referenceId: questionSetId,
      };

      // Envoyer à tous les membres du projet si projectId fourni
      let emailResult;
      if (projectId) {
        const { mainResult } = await emailService.sendEmailToProject(projectId, emailRequest);
        emailResult = mainResult;
      } else {
        emailResult = await emailService.sendEmail(emailRequest);
      }

      if (!emailResult.success) {
        throw new Error(emailResult.error || 'Erreur lors de l\'envoi de l\'email');
      }

      // Marquer l'email comme envoyé
      const { error: updateError } = await supabase
        .from('client_questions')
        .update({ email_sent_at: new Date().toISOString() })
        .eq('id', questionSetId);

      if (updateError) throw updateError;

      return { success: true, questionUrl };
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi de l\'email');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    createQuestionSet,
    getProjectQuestions,
    getQuestionResponses,
    getQuestionByToken,
    submitResponses,
    sendQuestionEmail,
    clearError: () => setError(null)
  };
}

// Fonction utilitaire pour générer un token d'accès
async function generateAccessToken(): Promise<string> {
  const { data, error } = await supabase.rpc('generate_client_access_token');
  if (error) throw error;
  return data;
}

// Fonction utilitaire pour récupérer l'IP du client
async function getClientIP(): Promise<string | null> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch {
    return null;
  }
}
